import styled, { ThemeProvider } from "styled-components"
import InputWithLabel from "../components/InputWithLabel"
import { useEffect, useState } from "react"
import PasswordInputWithLabel from "../components/PasswordInputWithLabel"
import WhiteSpace from "../components/Whitespace"
import Checkbox from "../components/Checkbox"
import Button from "../components/Button"
import { useNavigate, useSearchParams } from "react-router-dom"
import Dropdown from "../components/Dropdown"
import { LANGUAGES, languageByLabel, languageByCode, flagIcon } from "../languages"
import { useTranslation } from "react-i18next"
import GlobalStyle from "../GlobalStyle"
import { BREAKPOINTS, darkTheme, lightTheme } from "../GlobalTheme"
import { browserSessionPersistence, confirmPasswordReset, getAuth, sendPasswordResetEmail, setPersistence, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, OAuthProvider, createUserWithEmailAndPassword, sendEmailVerification, signInWithCustomToken, User } from "firebase/auth"
import fbConf from "../FirebaseConf"
import Comm from "../Comm"
import Icon from "../Icon"
import LoaderDots from "../components/LoaderDots"
import { FirebaseError } from "firebase/app"
import ensureUserProfile, { hasAcceptedTerms, SignInProvider } from "../utils/ensureUserProfile"
import { getPasskeyCustomToken, isPasskeyAvailable, isPasskeyCancelled } from "../utils/passkey"

const Container = styled.div`
  background-color: #181C32;
  width: 100vw;
  height: 100dvh;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    overflow-y: auto;
    overflow-x: hidden;
  }
`

const Shadow = styled.div<{ $color1: string, $color2: string, $left: string }>`
  position: absolute;
  width: 35vw;
  height: 35vw;
  left: ${props => props.$left};
  top: calc((100vh - 35vw) / 2);
  background-image: linear-gradient(${props => props.$color1} 0%, ${props => props.$color2} 100%);
  border-radius: 100vw;
  filter: blur(70px);
`

const ChargerFaceContainer = styled.div`
  position: absolute;
  width: 35vw;
  height: 35vw;
  left: 10vw;
  top: calc((100vh - 35vw) / 2);

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: none;
  }
`

const LoginContainer = styled.div`
  position: absolute;
  width: calc(30vw - 8vw);
  top: calc((100vh - 34vw) / 2);
  left: 60vw;
  background-color: ${props => props.theme.colors.white};
  padding: 4vw 4vw 2vw 4vw;
  border-radius: 1.25vw;
  box-shadow: 0px 0px 5px 5px #0C0C0D1A;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: relative;
    top: auto;
    left: auto;
    box-sizing: border-box;
    width: min(420px, calc(100% - 32px));
    margin: 24px auto 32px;
    padding: 24px 16px 16px;
    border-radius: 16px;
  }
`

const TitleLabel = styled.p`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.gray_900};
  font-weight: 600;
  font-size: 18px;
  margin-top: max(1.5vw, 19.2px);
  margin-bottom: max(1vw, 12.8px);
`

const SubtitleLabel = styled.p`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.gray_900};
  font-weight: 400;
  font-size: 14px;
  margin-bottom: max(1vw, 12.8px);
`

const SubtitleLabelLeft = styled.p`
  text-align: left;
  color: ${props => props.theme.colors.gray_700};
  font-weight: 400;
  font-size: 12px;
  margin-bottom: max(.5vw, 6.4px);
  width: 95%;
  margin-left: 2.5%;
`

const ForgotPasswordLabel = styled.p`
  float: right;
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.primary};
  margin-top: 0px;
  cursor: pointer;
`

const ErrorMessage = styled.p`
  width: 100%;
  text-align: left;
  font-size: 12px;
  color: ${props => props.theme.colors.danger};
`

const CopyRight = styled.p`
  position: absolute;
  color: #5E6278;
  font-weight: 400;
  font-size: 12px;
  width: 5vw;
  margin-left: 47.5vw;
  text-align: center;
  bottom: 5vh;
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    width: auto;
    margin: 0 0 24px;
    bottom: auto;
  }
`

const DropdownTable = styled.table`
  width: 100%;
`

const BackButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  left: 32px;
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 32px;
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const EmailLabel = styled.p`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.gray_900};
  font-weight: 600;
  font-size: 15px;
  margin-top: max(1.5vw, 19.2px);
  margin-bottom: max(1vw, 12.8px);
`

const PasswordStrengthIndicatorCell = styled.td<{ $status: string }>`
  height: 4px;
  border-radius: 10px;
  background-color: ${props => props.$status == 'red' ? props.theme.colors.danger : props.$status == 'green' ? props.theme.colors.success : props.theme.colors.gray_500};
`

const SignupLabel = styled.p`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.gray_700};
  font-weight: 500;
  font-size: 12px;
`

const SignupLabelBlue = styled.span`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
`

const AcceptPolicyLabel = styled.span`
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
`

export default function Login() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const [resettoken, setResettoken] = useState<string | null>(searchParams.get('oobCode'))

  const [emailInput, setEmailInput] = useState<string>('')
  const [passwordInput, setPasswordInput] = useState<string>('')
  const [isLoginWithEmailAndPassword, setIsLoginWithEmailAndPassword] = useState<boolean>(false)
  const [isRememberMe, setIsRememberMe] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false)
  const [forgotPasswordEmailInput, setForgotPasswordEmailInput] = useState<string>('')
  const [isRecoveryEmailSent, setIsRecoveryEmailSent] = useState<boolean>(false)
  const [newPasswordInput, setNewPasswordInput] = useState<string>('')
  const [newPasswordRepeatInput, setNewPasswordRepeatInput] = useState<string>('')
  const [newPasswordStrength, setNewPasswordStrength] = useState<number>(0) // between 0 and 4
  const [mousePos, setMousePos] = useState<{ x: number, y: number }>({ x: window.screen.width / 3, y: window.screen.height / 2 })
  const [isChargerWink, setIsChargerWink] = useState<boolean>(false)
  const [isUnknownEmailAtPasswordReset, setIsUnknownEmailAtPasswordReset] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [isSignup, setIsSignup] = useState<boolean>(false)
  const [isPPAccepted, setIsPPAccepted] = useState<boolean>(false)
  const [isTCAccepted, setIsTCAccepted] = useState<boolean>(false)
  const [isMarketingAccepted, setIsMarketingAccepted] = useState<boolean>(false)
  const [isConfrimationEmailSent, setIsConfirmationEmailSent] = useState<boolean>(false)
  const [pendingConsent, setPendingConsent] = useState<{ user: User, provider: SignInProvider } | null>(null)
  const [passkeyErrorMessage, setPasskeyErrorMessage] = useState<string | null>(null)

  const [theme, setTheme] = useState<string>(localStorage.getItem('preferedTheme') == 'system' ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : localStorage.getItem('preferedTheme') as string)

  useEffect(() => {
    getAuth(fbConf).onAuthStateChanged(user => {
      if (user) {
        let checkCounter = 0
        const checkIfTokenIsAvailableInterval = setInterval(() => {
          checkCounter += 1
          if (localStorage.getItem('token')) {
            clearInterval(checkIfTokenIsAvailableInterval)
            const redirectPath = searchParams.get("redirect") || "/" // Redirect the user to the path they were on before logging them out
            navigate(redirectPath, { replace: true })
          }
          if (checkCounter >= 100) {
            // Token not found after 10 seconds, something is not right
            clearInterval(checkIfTokenIsAvailableInterval)
          }
        }, 100)
      }
    })
  }, [])

  async function handleLogin(method: 'email' | 'apple' | 'google') {
    setIsLoading(true)

    const auth = getAuth(fbConf)

    // Only change persistence if user explicitly unchecks "Remember me"
    // Default is browserLocalPersistence (set in FirebaseConf.ts) — avoid redundant IndexedDB writes
    if (!isRememberMe) {
      await setPersistence(auth, browserSessionPersistence)
    }

    try {
      if (method == 'email') {
        await signInWithEmailAndPassword(auth, emailInput, passwordInput)
        await auth.currentUser?.reload()
        if (!auth.currentUser?.emailVerified) {
          await auth.signOut()
          setIsSignup(true)
          setIsConfirmationEmailSent(true)
          setIsLoading(false)
          setErrorMessage(null)
          return
        }

      } else if (method == 'google') {
        //await signInWithRedirect(auth, new GoogleAuthProvider())
        await signInWithPopup(auth, new GoogleAuthProvider())
      } else if (method == 'apple') {
        await signInWithPopup(auth, new OAuthProvider('apple.com'))
      }
      setErrorMessage(null)

      // Google and Apple users never got a profile node before VLT-2669, and an older email account
      // can be missing fields the mobile apps expect. Kept out of the catch below so a failed write
      // reports itself instead of being shown as a wrong password.
      const provider: SignInProvider = method == 'email' ? 'password' : method

      // A first time Google or Apple user has never been shown the terms, so hold them on the consent
      // screen before any token is stored - the redirect effect only fires once one is (VLT-2669).
      if (provider != 'password' && auth.currentUser && !(await hasAcceptedTerms(auth.currentUser.uid))) {
        setPendingConsent({ user: auth.currentUser, provider })
        setIsPPAccepted(false)
        setIsTCAccepted(false)
        setIsMarketingAccepted(false)
        setIsLoading(false)
        return
      }

      if (auth.currentUser) {
        try {
          await ensureUserProfile(auth.currentUser, provider)
        } catch (err) {
          console.log('Unable to ensure the user profile node', err)
        }
      }


      await finishSignIn()
    } catch (error) {
      console.log(error)
      setErrorMessage(t('login_incorrectEmailOrPassword'))
      setPasswordInput('')
      setIsLoading(false)
    }
  }

  // A passkey is only ever registered from a signed-in session, so its owner already has a profile and
  // has been through the terms screen; this skips straight to the profile heal and the token.
  async function handlePasskeyLogin() {
    setIsLoading(true)
    const auth = getAuth(fbConf)
    if (!isRememberMe) {
      await setPersistence(auth, browserSessionPersistence)
    }

    setPasskeyErrorMessage(null)
    try {
      const { user } = await signInWithCustomToken(auth, await getPasskeyCustomToken())
      try {
        await ensureUserProfile(user, null)
      } catch (err) {
        console.log('Unable to ensure the user profile node', err)
      }
      await finishSignIn()
    } catch (err) {
      if (!isPasskeyCancelled(err)) {
        console.log('Unable to sign in with a passkey', err)
        setPasskeyErrorMessage(t('login_passkeySignInFailedPleaseTryAgain'))
      }
      setIsLoading(false)
    }
  }

  // Stores the token the redirect effect waits for; the last step of every sign-in path.
  async function finishSignIn() {
    const auth = getAuth(fbConf)
    // If user is admin, get the admin token from the server, if not use the token from firebase as the token, either way, save the token to localStorage
    if ((await auth.currentUser?.getIdTokenResult())?.claims.admin) {
      const firebaseToken = await auth.currentUser?.getIdToken()
      const response = await Comm('/api-admin/login/admin', { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${firebaseToken}` } })
      const token = await response?.text()
      if (token) { localStorage.setItem('token', token) }
    } else {

      const idToken = await auth.currentUser?.getIdToken()
      if (idToken) {
        localStorage.setItem('token', idToken)
      }
    }

    localStorage.setItem('lastLogin', new Date().toString().split(' ').splice(1, 4).join(' '))

    // The onAuthStateChanged effect only watches for the token for 10 seconds after the auth state
    // changes, which the consent screen outlives, so the redirect is done here rather than left to it.
    navigate(searchParams.get("redirect") || "/", { replace: true })
  }

  async function handleAcceptConsent() {
    if (!pendingConsent) return

    if (!isTCAccepted || !isPPAccepted) {
      setErrorMessage(t('login_pleaseAcceptOurTermsAndConditionsAndPrivacyPolicyToContinue'))
      return
    }

    setIsLoading(true)
    try {
      await ensureUserProfile(pendingConsent.user, pendingConsent.provider, {
        marketingAccepted: isMarketingAccepted,
        acceptedTermsVersion: (new Date()).toISOString(),
      })
      setPendingConsent(null)
      setErrorMessage(null)
      await finishSignIn()
    } catch (err) {
      console.log('Unable to save the accepted terms', err)
      setErrorMessage(err instanceof Error ? err.message : t('login_unknownError'))
      setIsLoading(false)
    }
  }

  // Declining leaves a Firebase Auth record with no profile, which is exactly what an unaccepted
  // signup should be; signing out keeps them off the dashboard.
  async function handleDeclineConsent() {
    setPendingConsent(null)
    setErrorMessage(null)
    setIsLoading(false)
    await getAuth(fbConf).signOut()
  }

  async function handleSignup() {
    const auth = getAuth(fbConf)

    try {

      if (!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput))) {
        setErrorMessage(t('login_invalidEmail'))
        return
      }

      if (newPasswordStrength <= 0) {
        setErrorMessage(t('login_passwordTooWeak'))
        return
      }

      if (!isTCAccepted || !isPPAccepted) {
        setErrorMessage(t('login_pleaseAcceptOurTermsAndConditionsAndPrivacyPolicyToContinue'))
        return
      }

      setIsLoading(true)

      const userCredential = await createUserWithEmailAndPassword(auth, emailInput, newPasswordInput)
      const user = userCredential.user
      await sendEmailVerification(user)

      await ensureUserProfile(user, 'password', {
        marketingAccepted: isMarketingAccepted,
        acceptedTermsVersion: (new Date()).toISOString(),
      })

      // Sign the user out so we can check if they verified their email
      await auth.signOut()
      setErrorMessage(null)
      setIsLoading(false)
      setIsConfirmationEmailSent(true)
      setPasswordInput('')

    } catch (err) {
      if (err instanceof FirebaseError) {
        setErrorMessage(err.code.split("/")[1].split("-").join(" "))
      } else {
        setErrorMessage(t('login_unregisteredEmail'))
      }
      throw new Error(`Unable to complete signup: ${err}`)
    }
  }

  // Set language to preferred
  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('preferedLanguage') || 'en')
  }, [])

  function handleChooseDropdownOption(label: string) {
    // The Dropdown reports the chosen label, so the language list resolves it back to a code.
    const language = languageByLabel(label)
    if (language) {
      i18n.changeLanguage(language.code)
      localStorage.setItem('preferedLanguage', language.code)
      return
    }

    switch (label) {
      case t('login_light'):
        setTheme('light')
        localStorage.setItem('preferedTheme', 'light')
        break
      case t('login_dark'):
        setTheme('dark')
        localStorage.setItem('preferedTheme', 'dark')
        break
      case t('login_system'):
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          setTheme('dark')
        } else {
          setTheme('light')
        }
        localStorage.setItem('preferedTheme', 'system')
        break
      case 'help@voltie.eu':
        window.open(`mailto:help@voltie.eu?subject=Voltie Dashboard visszajelzés`)
    }
  }

  async function handleSubmitForgotPassword() {
    try {
      await sendPasswordResetEmail(getAuth(fbConf), forgotPasswordEmailInput)
      setIsRecoveryEmailSent(true)
      setIsUnknownEmailAtPasswordReset(false)
    } catch (err) {
      setIsUnknownEmailAtPasswordReset(true)
    }
  }

  async function handleResetPassword() {
    setErrorMessage(newPasswordInput != newPasswordRepeatInput ? t('login_twoPasswordsDoNotMatch') : null)
    setErrorMessage(newPasswordStrength < 1 ? t('login_passwordTooWeak') : null)

    if (newPasswordInput == newPasswordRepeatInput && newPasswordStrength > 0 && resettoken) {
      await confirmPasswordReset(getAuth(fbConf), resettoken, newPasswordInput)
      setIsForgotPassword(false)
      setIsRecoveryEmailSent(false)
      setResettoken(null)
      navigate('/login')
    }
  }

  // new password validation
  useEffect(() => {
    const passowordValidationRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.#$!%*?&^])[A-Za-z\d@.#$!%*?&]{8,100}$/
    let newPasswordStrength = 0
    if (passowordValidationRegex.test(newPasswordInput)) {
      newPasswordStrength = 1
      if (newPasswordInput.length > 20) {
        newPasswordStrength = 4
      } else if (newPasswordInput.length > 15) {
        newPasswordStrength = 3
      } else if (newPasswordInput.length > 10) {
        newPasswordStrength = 2
      }
    }
    setNewPasswordStrength(newPasswordStrength)
  }, [newPasswordInput])

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    setMousePos({ x: e.pageX, y: e.pageY })
  }

  function handleStartWink() {
    setIsChargerWink(true)
    setTimeout(() => setIsChargerWink(false), 300)
  }

  // Detect light/dark theme change
  useEffect(() => {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
      if (localStorage.getItem('preferedTheme') == 'system') {
        event.matches ? setTheme('dark') : setTheme('light')
      }
    })
  }, [])

  return (
    <ThemeProvider theme={theme == 'dark' ? darkTheme : lightTheme}>
      <GlobalStyle />
      <Container onMouseMove={handleMouseMove} onClick={handleStartWink}>
        <Shadow $color1='rgba(0, 194, 255, .1)' $color2='#4AB1FC' $left='10vw' />
        <Shadow $color1='rgba(0, 194, 255, .4)' $color2='#901b6f' $left='55vw' />
        <ChargerFaceContainer>
          <Icon path="login.chargerFace" width="100%" height="100%" />
        </ChargerFaceContainer>
        <ChargerFaceContainer style={{ marginLeft: `${(mousePos.x - (window.screen.width / 3)) / 25}px`, marginTop: `${(mousePos.y - (window.screen.height / 2)) / 25}px` }}>
          <Icon path={isChargerWink ? "login.chargerWink" : "login.chargerEye"} width="100%" height="100%" />
        </ChargerFaceContainer>
        <LoginContainer>
          {pendingConsent ? <>
            {/* Terms and privacy for a first time Google or Apple user, who never sees the signup form (VLT-2669) */}
            <Icon path="general.logoDashboardVertical" width="calc((100% + max(8vw, 102.4px)) * .32)" marginleft="calc((100% - ((100% + max(8vw, 102.4px)) * .32)) / 2)" />
            <TitleLabel>{t('login_signUp')}</TitleLabel>
            <SubtitleLabelLeft>{t('login_pleaseAcceptOurTermsAndConditionsAndPrivacyPolicyToContinue')}</SubtitleLabelLeft>
            {errorMessage && (
              <ErrorMessage>{errorMessage}</ErrorMessage>
            )}
            <WhiteSpace height="max(1vw, 12.8px)" />
            <Checkbox label={
              <>* {t('login_iHaveReadAndAcceptedThe')} <AcceptPolicyLabel onClick={() => window.open('https://www.voltie.eu/en/documents', '_blank')}>{t('login_privacyPolicy')}</AcceptPolicyLabel></>
            } value={isPPAccepted} onClick={() => setIsPPAccepted(!isPPAccepted)} />
            <WhiteSpace height="max(.5vw, 6.4px)" />
            <Checkbox label={
              <>* {t('login_iHaveReadAndAcceptedThe')} <AcceptPolicyLabel onClick={() => window.open('https://www.voltie.eu/en/documents', '_blank')}>{t('login_termsAndConditions')}</AcceptPolicyLabel></>
            } value={isTCAccepted} onClick={() => setIsTCAccepted(!isTCAccepted)} />
            <WhiteSpace height="max(.5vw, 6.4px)" />
            <Checkbox label={t('login_iAgreeToReceiveMarketingEmails')} value={isMarketingAccepted} onClick={() => setIsMarketingAccepted(!isMarketingAccepted)} />
            <WhiteSpace height="max(2vw, 25.6px)" />
            <Button onclick={handleAcceptConsent} label={isLoading ? <LoaderDots /> : t('general_confirm')} width='100%' />
            <WhiteSpace height="max(1vw, 12.8px)" />
            <Button onclick={handleDeclineConsent} bgcolor="transparent" labelcolor="dark" bordercolor="#181C32" label={t('general_cancel')} width='100%' />
          </> : !isSignup ? <>
            {!resettoken ? (
              <>
                {!isForgotPassword && (
                  <>
                    {isLoginWithEmailAndPassword ? <>
                      {/* Standard Email And Password Login Page */}
                      <BackButtonContainer onClick={() => setIsLoginWithEmailAndPassword(false)}>
                        <Icon path="general.backArrow" width="100%" height="100%" />
                      </BackButtonContainer>
                      <Icon path="general.logoDashboardVertical" width="calc((100% + max(8vw, 102.4px)) * .32)" marginleft="calc((100% - ((100% + max(8vw, 102.4px)) * .32)) / 2)" />
                      <TitleLabel>{t('login_signIn')}</TitleLabel>
                      <InputWithLabel value={emailInput} onInput={(value) => setEmailInput(value)} label='Email *' width="100%" />
                      <WhiteSpace height="max(1vw, 12.8px)" />
                      <PasswordInputWithLabel value={passwordInput} onInput={(value) => setPasswordInput(value)} label={`${t('login_password')} *`} width="100%" onKeyDown={(e) => { if (e.key === 'Enter') handleLogin('email') }} />
                      {errorMessage && (
                        <ErrorMessage>{errorMessage}</ErrorMessage>
                      )}
                      <WhiteSpace height="max(1vw, 12.8px)" />
                      <ForgotPasswordLabel onClick={() => setIsForgotPassword(true)}>{t('login_forgotPassword')}?</ForgotPasswordLabel>
                      <Checkbox label={t('login_rememberMe')} value={isRememberMe} onClick={() => setIsRememberMe(!isRememberMe)} />
                      <WhiteSpace height="max(2vw, 25.6px)" />
                      <Button onclick={() => handleLogin('email')} label={isLoading ? <LoaderDots /> : t('login_signIn')} width='100%' />
                    </> : <>
                      {/* Login Method Chooser Page (Google, Apple, Email) */}
                      <Icon path="general.logoDashboardVertical" width="calc((100% + max(8vw, 102.4px)) * .32)" marginleft="calc((100% - ((100% + max(8vw, 102.4px)) * .32)) / 2)" />
                      <TitleLabel>{t('login_goodToSeeYou')}</TitleLabel>
                      <Button onclick={() => handleLogin('apple')} label={<>
                        <Icon path={"login.appleLogoSmall"} width="18px" height="18px" margintop="-4px" marginbottom="-4px" marginright="12px" color="white" />
                        {t('login_continueWithApple')}
                      </>} width='100%' />
                      <WhiteSpace height="max(1vw, 12.8px)" />
                      <Button onclick={() => handleLogin('google')} bgcolor="transparent" labelcolor="dark" bordercolor="#181C32" label={<>
                        <Icon path={"login.googleLogoSmall"} width="18px" height="18px" margintop="-4px" marginbottom="-4px" marginright="12px" color="white" />
                        {t('login_continueWithGoogle')}
                      </>} width='100%' />
                      <WhiteSpace height="max(1vw, 12.8px)" />
                      {isPasskeyAvailable() && <>
                        <Button onclick={handlePasskeyLogin} bgcolor="transparent" labelcolor="dark" bordercolor="#181C32" label={<>
                          <Icon path={"app.key"} width="18px" height="18px" margintop="-4px" marginbottom="-4px" marginright="12px" color="primary" />
                          {t('login_continueWithPasskey')}
                        </>} width='100%' />
                        <WhiteSpace height="max(1vw, 12.8px)" />
                      </>}
                      {passkeyErrorMessage && (
                        <ErrorMessage>{passkeyErrorMessage}</ErrorMessage>
                      )}
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: '42.5%', height: '0px', marginTop: '7px', borderBottom: '1px solid #E1E3EA' }} />
                        <p style={{ width: '15%', textAlign: 'center', margin: '0px', fontWeight: '400', fontSize: '14px', color: '#A1A5B7' }}>{t('login_or')}</p>
                        <div style={{ width: '42.5%', height: '0px', marginTop: '7px', borderBottom: '1px solid #E1E3EA' }} />
                      </div>
                      <WhiteSpace height="max(1vw, 12.8px)" />
                      <Button onclick={() => setIsLoginWithEmailAndPassword(true)} bgcolor="transparent" labelcolor="dark" bordercolor="#181C32" label={<>
                        <Icon path={"login.mail"} width="18px" height="18px" margintop="-4px" marginbottom="-4px" marginright="12px" color="primary" />
                        {t('login_continueWithEmail')}
                      </>} width='100%' />
                      <WhiteSpace height="max(1vw, 12.8px)" />
                      <div>
                        <SignupLabel>{t('login_dontHaveAnAccountYet')} <SignupLabelBlue onClick={() => { setIsSignup(true); setErrorMessage(null) }}>{t('login_signUp')}!</SignupLabelBlue></SignupLabel>
                      </div>
                    </>}
                  </>
                )}
                {isForgotPassword && (
                  <>
                    {!isRecoveryEmailSent ? (
                      <>
                        {/* Page To Enter Email Address For Password Recovery */}
                        <BackButtonContainer onClick={() => setIsForgotPassword(false)}>
                          <Icon path="general.backArrow" width="100%" height="100%" />
                        </BackButtonContainer>
                        <CloseButtonContainer onClick={() => setIsForgotPassword(false)}>
                          <Icon path="general.x" width="100%" height="100%" />
                        </CloseButtonContainer>
                        <Icon path="login.forgotPassword" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
                        <TitleLabel>{t('login_forgotPassword')}</TitleLabel>
                        <SubtitleLabel>{t('login_enterEmailOrPhoneNumberUsedDuringSignUp')}</SubtitleLabel>
                        <WhiteSpace height="max(.5vw, 6.4px)" />
                        <InputWithLabel value={forgotPasswordEmailInput} onInput={(value) => setForgotPasswordEmailInput(value)} label='Email *' width="100%" isError={isUnknownEmailAtPasswordReset} />
                        {isUnknownEmailAtPasswordReset && (
                          <ErrorMessage>{t('login_unknownEmail')}</ErrorMessage>
                        )}
                        <WhiteSpace height="max(2vw, 25.6px)" />
                        <Button onclick={handleSubmitForgotPassword} label={t('login_submit')} width='100%' />
                      </>
                    ) : (
                      <>
                        {/* Page That Informs User That The Password Recovery Email Has Been Sent */}
                        <BackButtonContainer onClick={() => setIsRecoveryEmailSent(false)}>
                          <Icon path="general.backArrow" width="100%" height="100%" />
                        </BackButtonContainer>
                        <CloseButtonContainer onClick={() => { setIsForgotPassword(false); setIsRecoveryEmailSent(false) }}>
                          <Icon path="general.x" width="100%" height="100%" />
                        </CloseButtonContainer>
                        <Icon path="login.checkYourInbox" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
                        <TitleLabel>{t('login_checkYourEmail')}</TitleLabel>
                        <SubtitleLabel>{t('login_sentInstructionsOnHowToResetYourPassword')}</SubtitleLabel>
                        <EmailLabel>{forgotPasswordEmailInput}</EmailLabel>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {/* Page To Set A New Password (currently unused, because firebase cant redirect to our custom reset link, beacuse mobile uses the same thing) */}
                <Icon path="login.setNewPassword" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
                <TitleLabel>{t('login_setupNewPassword')}</TitleLabel>
                <PasswordInputWithLabel value={newPasswordInput} onInput={(value) => setNewPasswordInput(value)} label={`${t('login_password')} *`} width="100%" />
                <WhiteSpace height="max(.5vw, 6.4px)" />
                <table style={{ width: '95%', marginLeft: '2.5%' }}>
                  <tbody>
                    <tr>
                      <PasswordStrengthIndicatorCell $status={newPasswordStrength == 0 ? newPasswordInput.length > 0 ? 'red' : 'gray' : 'green'} ></PasswordStrengthIndicatorCell>
                      <PasswordStrengthIndicatorCell $status={newPasswordStrength > 1 ? 'green' : 'gray'} ></PasswordStrengthIndicatorCell>
                      <PasswordStrengthIndicatorCell $status={newPasswordStrength > 2 ? 'green' : 'gray'} ></PasswordStrengthIndicatorCell>
                      <PasswordStrengthIndicatorCell $status={newPasswordStrength > 3 ? 'green' : 'gray'} ></PasswordStrengthIndicatorCell>
                    </tr>
                  </tbody>
                </table>
                <SubtitleLabelLeft>{t('login_use8OrMoreCharactersWithAMixOfLettersNumbersSymbols')}</SubtitleLabelLeft>
                <PasswordInputWithLabel value={newPasswordRepeatInput} onInput={(value) => setNewPasswordRepeatInput(value)} label={`${t('login_repeatPassword')} *`} width="100%" />
                <WhiteSpace height="max(2vw, 25.6px)" />
                <Button onclick={handleResetPassword} label={t('login_save')} width='100%' />
              </>
            )}
          </> : !isConfrimationEmailSent ? <>
            {/* Signup */}
            <BackButtonContainer onClick={() => setIsSignup(false)}>
              <Icon path="general.backArrow" width="100%" height="100%" />
            </BackButtonContainer>
            <Icon path="general.logoDashboardVertical" width="calc((100% + max(8vw, 102.4px)) * .32)" marginleft="calc((100% - ((100% + max(8vw, 102.4px)) * .32)) / 2)" />
            <TitleLabel>{t('login_signUp')}</TitleLabel>
            <InputWithLabel value={emailInput} onInput={(value) => setEmailInput(value)} label='Email *' width="100%" />
            <WhiteSpace height="max(1vw, 12.8px)" />
            <PasswordInputWithLabel value={newPasswordInput} onInput={(value) => setNewPasswordInput(value)} label={`${t('login_password')} *`} width="100%" />
            <WhiteSpace height="max(.5vw, 6.4px)" />
            <table style={{ width: '95%', marginLeft: '2.5%' }}>
              <tbody>
                <tr>
                  <PasswordStrengthIndicatorCell $status={newPasswordStrength == 0 ? newPasswordInput.length > 0 ? 'red' : 'gray' : 'green'} ></PasswordStrengthIndicatorCell>
                  <PasswordStrengthIndicatorCell $status={newPasswordStrength > 1 ? 'green' : 'gray'} ></PasswordStrengthIndicatorCell>
                  <PasswordStrengthIndicatorCell $status={newPasswordStrength > 2 ? 'green' : 'gray'} ></PasswordStrengthIndicatorCell>
                  <PasswordStrengthIndicatorCell $status={newPasswordStrength > 3 ? 'green' : 'gray'} ></PasswordStrengthIndicatorCell>
                </tr>
              </tbody>
            </table>
            <SubtitleLabelLeft>{t('login_use8OrMoreCharactersWithAMixOfLettersNumbersSymbols')}</SubtitleLabelLeft>
            {errorMessage && (
              <ErrorMessage>{errorMessage}</ErrorMessage>
            )}
            <WhiteSpace height="max(1vw, 12.8px)" />
            <Checkbox label={
              <>* {t('login_iHaveReadAndAcceptedThe')} <AcceptPolicyLabel onClick={() => window.open('https://www.voltie.eu/en/documents', '_blank')}>{t('login_privacyPolicy')}</AcceptPolicyLabel></>
            } value={isPPAccepted} onClick={() => setIsPPAccepted(!isPPAccepted)} />
            <WhiteSpace height="max(.5vw, 6.4px)" />
            <Checkbox label={
              <>* {t('login_iHaveReadAndAcceptedThe')} <AcceptPolicyLabel onClick={() => window.open('https://www.voltie.eu/en/documents', '_blank')}>{t('login_termsAndConditions')}</AcceptPolicyLabel></>
            } value={isTCAccepted} onClick={() => setIsTCAccepted(!isTCAccepted)} />
            <WhiteSpace height="max(.5vw, 6.4px)" />
            <Checkbox label={t('login_iAgreeToReceiveMarketingEmails')} value={isMarketingAccepted} onClick={() => setIsMarketingAccepted(!isMarketingAccepted)} />
            <WhiteSpace height="max(2vw, 25.6px)" />
            <Button onclick={handleSignup} label={isLoading ? <LoaderDots /> : t('login_signUp')} width='100%' />
          </> : <>
            {/* Signup confirmation email sent page */}
            <>
              <CloseButtonContainer onClick={() => { setIsConfirmationEmailSent(false); setIsSignup(false) }}>
                <Icon path="general.x" width="100%" height="100%" />
              </CloseButtonContainer>
              <Icon path="login.checkYourInbox" width="96px" height="96px" marginleft="calc((100% - 96px) / 2)" />
              <TitleLabel>{t('login_checkYourEmail')}</TitleLabel>
              <EmailLabel>{emailInput}</EmailLabel>
            </>
          </>}
          <WhiteSpace height="max(1vw, 12.8px)" />
          <DropdownTable>
            <tbody>
              <tr>
                <td style={{ width: '33%', paddingLeft: '.5vw' }}>
                  <Dropdown showOptionContainerOnTop handleChoose={handleChooseDropdownOption} title={{ icon: flagIcon(languageByCode(i18n.language)?.code || 'en'), label: languageByCode(i18n.language)?.label || 'English' }} options={LANGUAGES.map(language => ({ icon: flagIcon(language.code), label: language.label }))} />
                </td>
                <td style={{ width: '33%', paddingLeft: '.75vw' }}>
                  <Dropdown handleChoose={handleChooseDropdownOption} title={{ icon: localStorage.getItem('preferedTheme') == 'system' ? "login.systemTheme" : theme == 'dark' ? "login.darkmode" : "login.lightmode", label: t('login_mode'), color: "gray_600" }} options={[{ icon: "login.lightmode", label: t('login_light'), color: "gray_600" }, { icon: "login.darkmode", label: t('login_dark'), color: "gray_600" }, { icon: "login.systemTheme", label: t('login_system'), color: "gray_600" }]} />
                </td>
                <td style={{ width: '33%' }}>
                  <Dropdown handleChoose={handleChooseDropdownOption} title={{ icon: "login.mail", label: t('login_contactUs'), color: "gray_600" }} options={[{ icon: "login.mail", label: 'help@voltie.eu', color: "gray_600" }]} />
                </td>
              </tr>
            </tbody>
          </DropdownTable>
        </LoginContainer>
        <a href="https://www.voltie.eu/">
          <CopyRight>© 2026 Voltie</CopyRight>
        </a>
      </Container>
    </ThemeProvider>
  )
}
